import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

interface NewsItem {
  id: number;
  title: string;
  content: string;
  image_path: string;
  created_at?: string;
}

const NewsSlider: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const loadNews = useCallback(async () => {
    try {
      const response = await fetch(
        "https://api-aethel.yoyooinc.com/api/v1/news/all",
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const newsList: NewsItem[] = await response.json();
      setNews(newsList);
      setError(null);
    } catch (err) {
      console.error("Error cargando noticias:", err);
      setError(t("home", "news.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % news.length);
  }, [news.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
  }, [news.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("es-ES", options);
  };

  if (loading) {
    return (
      <section className="news-section">
        <h2 className="section-title">{t("home", "news.title")}</h2>
        <div className="news-container">
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <i className="fas fa-spinner fa-spin"></i>
            <p>{t("home", "loading")}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="news-section">
        <h2 className="section-title">{t("home", "news.title")}</h2>
        <div className="news-container">
          <div
            className="news-error"
            style={{ padding: "2rem", textAlign: "center" }}
          >
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (news.length === 0) {
    return (
      <section className="news-section">
        <h2 className="section-title">{t("home", "news.title")}</h2>
        <div className="news-container">
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <p>{t("home", "news.noNews")}</p>
          </div>
        </div>
      </section>
    );
  }

  const currentNews = news[currentSlide];
  const mediaExtension = currentNews.image_path.split(".").pop()?.toLowerCase();
  const isVideo = ["mp4", "webm", "ogg", "mov"].includes(mediaExtension || "");

  return (
    <section className="news-section">
      <h2 className="section-title">{t("home", "news.title")}</h2>

      <div className="slider-dots">
        {news.map((_, index) => (
          <span
            key={index}
            className={`dot ${index === currentSlide ? "active" : ""}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>

      <div className="news-container">
        <div
          className="news-slider"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {news.map((item) => (
            <div key={item.id} className="news-slide">
              <div className="news-card">
                <div className="news-header">
                  <h3 className="news-title">{item.title}</h3>
                  {item.created_at && (
                    <span className="news-date">
                      {formatDate(item.created_at)}
                    </span>
                  )}
                </div>

                <div className="news-content">
                  {isVideo ? (
                    <video controls className="news-media news-video">
                      <source
                        src={item.image_path}
                        type={`video/${mediaExtension}`}
                      />
                      {t("home", "news.noVideoSupport")}
                    </video>
                  ) : (
                    <img
                      src={item.image_path}
                      alt={item.title}
                      className="news-media"
                    />
                  )}

                  <p className="news-text">
                    {item.content.substring(0, 150)}...
                  </p>
                </div>

                <button className="news-read-more">
                  {t("home", "news.readMore")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="slider-controls">
        <button className="slider-btn prev-btn" onClick={prevSlide}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <button className="slider-btn next-btn" onClick={nextSlide}>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    </section>
  );
};

export default NewsSlider;
